// frontend/src/pages/admin/BoardManagement/BoardManagement.jsx

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/common/Button';
import GradientButton from '../../../components/common/GradientButton';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import JalaliDatePicker from '../../../components/common/JalaliDatePicker';
import Modal from '../../../components/common/Modal';
import Card from '../../../components/common/Card';
import FileUpload from '../../../components/common/FileUpload';
import StatusBadge from '../../../components/common/StatusBadge';
import { 
  getPositionsApi, 
  createPositionApi, 
  updatePositionApi, 
  deletePositionApi,
  uploadPositionImageApi,
} from '../../../api/position.api';
import { getUsersApi } from '../../../api/user.api';
import { POSITION_TYPE_LABELS, POSITION_STATUS_LABELS } from '../../../utils/constants';
import { toJalaliFull } from '../../../utils/jalali';

// ============================================================
// فرم خالی
// ============================================================
const emptyForm = {
  title: '',
  type: 'BOARD',
  firstName: '',
  lastName: '',
  userId: '',
  startDate: '',
  endDate: '',
  status: 'ACTIVE',
  description: '',
  order: '',
  bio: '',
  image: '',
  education: '',
  fieldOfStudy: '',
  workExperience: '',
  phone: '',
  email: '',
  birthDate: '',
  birthPlace: '',
};

export default function BoardManagement() {
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploading, setUploading] = useState(false);

  // =========================================================
  // بارگذاری داده‌ها
  // =========================================================

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [positionsRes, usersRes] = await Promise.all([
        getPositionsApi({ type: 'BOARD' }),
        getUsersApi({ limit: 100 }),
      ]);
      setMembers(positionsRes.data.data || []);
      setUsers(usersRes.data.data || []);
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت اطلاعات');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // =========================================================
  // مدیریت فرم
  // =========================================================

  const openCreateForm = () => {
    setEditing(null);
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview(null);
    setError('');
    setFormOpen(true);
  };

  const openEditForm = (member) => {
    setEditing(member);
    setForm({
      title: member.title || '',
      type: member.type || 'BOARD',
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      userId: member.userId || '',
      startDate: member.startDate || '',
      endDate: member.endDate || '',
      status: member.status || 'ACTIVE',
      description: member.description || '',
      order: member.order || '',
      bio: member.bio || '',
      image: member.image || '',
      education: member.education || '',
      fieldOfStudy: member.fieldOfStudy || '',
      workExperience: member.workExperience || '',
      phone: member.phone || '',
      email: member.email || '',
      birthDate: member.birthDate || '',
      birthPlace: member.birthPlace || '',
    });
    setImagePreview(member.image ? getImageUrl(member.image) : null);
    setImageFile(null);
    setError('');
    setFormOpen(true);
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const base = process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '') || '';
    return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  // =========================================================
  // محاسبه نام کامل برای نمایش
  // =========================================================
  const getFullName = (member) => {
    if (member.user?.fullName) return member.user.fullName;
    if (member.firstName || member.lastName) {
      return `${member.firstName || ''} ${member.lastName || ''}`.trim();
    }
    return 'بدون نام';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // اعتبارسنجی
    if (!form.title) {
      setError('عنوان سمت الزامی است');
      return;
    }

    // حداقل یکی از userId یا firstName+lastName باید پر باشد
    const hasUser = form.userId && form.userId !== '';
    const hasName = (form.firstName && form.firstName.trim()) || (form.lastName && form.lastName.trim());
    if (!hasUser && !hasName) {
      setError('حداقل یکی از فیلدهای "کاربر" یا "نام و نام خانوادگی" باید پر شود');
      return;
    }

    setUploading(true);

    try {
      const payload = {
        ...form,
        userId: form.userId ? Number(form.userId) : null,
        order: form.order ? Number(form.order) : 0,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        birthDate: form.birthDate || null,
        // اگر firstName و lastName خالی باشند، null ارسال می‌کنیم
        firstName: form.firstName?.trim() || null,
        lastName: form.lastName?.trim() || null,
      };

      let response;
      if (editing) {
        response = await updatePositionApi(editing.id, payload);
      } else {
        response = await createPositionApi(payload);
      }

      const positionId = response.data.data?.id || editing?.id;

      if (imageFile && positionId) {
        await uploadPositionImageApi(positionId, imageFile);
      }

      setSnackbar(editing ? 'عضو با موفقیت ویرایش شد' : 'عضو با موفقیت ایجاد شد');
      setFormOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'عملیات ناموفق بود');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePositionApi(deleteTarget);
      setSnackbar('عضو با موفقیت حذف شد');
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'حذف ناموفق بود');
    }
  };

  // =========================================================
  // رندر
  // =========================================================

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-text-dark">هیئت رئیسه و کمیته‌ ها</h1>
          <p className="text-sm text-slate-400">مدیریت اعضای هیئت رئیسه و کمیته‌ های تخصصی</p>
        </div>
        <GradientButton onClick={openCreateForm} className="w-full sm:w-auto">
          <Icon name="plus" className="ml-2 h-4 w-4" />
          عضو جدید
        </GradientButton>
      </div>

      {/* Snackbar */}
      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700 animate-fade-in flex items-center justify-between">
          <span>{snackbar}</span>
          <button className="mr-2 text-emerald-500 hover:text-emerald-700" onClick={() => setSnackbar('')}>✕</button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      ) : (
        <>
          {/* Members Grid - Responsive */}
          {members.length === 0 ? (
            <Card className="p-10 text-center">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-slate-100 text-slate-300">
                <Icon name="users" className="h-8 w-8" />
              </div>
              <h3 className="mt-4 text-lg font-black text-slate-700">هیچ عضوی ثبت نشده است</h3>
              <p className="mt-2 text-sm text-slate-400">برای شروع، اولین عضو هیئت رئیسه را اضافه کنید.</p>
              <GradientButton onClick={openCreateForm} className="mt-4">
                <Icon name="plus" className="ml-2 h-4 w-4" />
                افزودن عضو
              </GradientButton>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {members.map((member) => {
                const fullName = getFullName(member);
                const avatar = getImageUrl(member.image || member.user?.avatar);
                const initials = fullName.charAt(0);

                return (
                  <Card
                    key={member.id}
                    hover
                    className="group overflow-hidden p-0 transition-all duration-500 hover:-translate-y-2 flex flex-col h-full"
                  >
                    {/* Header with image */}
                    <div className="relative h-28 bg-gradient-to-br from-emerald-500 to-emerald-700 shrink-0">
                      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

                      {/* Avatar */}
                      <div className="absolute -bottom-8 right-6">
                        <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-white bg-white shadow-xl transition-transform duration-300 group-hover:scale-105">
                          {avatar ? (
                            <img 
                              src={avatar} 
                              alt={fullName} 
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-100 to-emerald-200 text-2xl font-black text-emerald-700">
                              {initials}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Badge */}
                      <div className="absolute left-4 top-4 rounded-full border border-white/20 bg-black/20 px-3 py-1 text-[10px] font-bold text-white backdrop-blur-sm">
                        {POSITION_TYPE_LABELS[member.type] || member.type}
                      </div>
                    </div>

                    {/* Content - grows to fill space */}
                    <div className="p-6 pt-12 flex flex-col flex-1">
                      <h3 className="text-lg font-black text-text-dark line-clamp-1">{member.title}</h3>
                      <p className="text-sm text-slate-500 line-clamp-1">{fullName}</p>

                      {member.description && (
                        <p className="mt-2 text-sm leading-6 text-slate-400 line-clamp-2">
                          {member.description}
                        </p>
                      )}

                      {/* Extra info - condensed */}
                      <div className="mt-3 space-y-1 text-xs text-slate-400">
                        {member.education && (
                          <p className="line-clamp-1">🎓 {member.education}</p>
                        )}
                        {member.fieldOfStudy && (
                          <p className="line-clamp-1">📚 {member.fieldOfStudy}</p>
                        )}
                        {member.phone && (
                          <p className="line-clamp-1">📞 {member.phone}</p>
                        )}
                        {member.email && (
                          <p className="line-clamp-1">✉️ {member.email}</p>
                        )}
                        {member.birthDate && (
                          <p>🎂 {toJalaliFull(member.birthDate)}</p>
                        )}
                        {member.birthPlace && (
                          <p className="line-clamp-1">📍 {member.birthPlace}</p>
                        )}
                      </div>

                      {/* Metadata */}
                      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-400">
                        {member.startDate && (
                          <span className="flex items-center gap-1">
                            <Icon name="calendar" className="h-3.5 w-3.5" />
                            {toJalaliFull(member.startDate)}
                          </span>
                        )}
                        {member.order !== null && member.order !== undefined && member.order !== 0 && (
                          <span className="flex items-center gap-1">
                            <Icon name="hash" className="h-3.5 w-3.5" />
                            {member.order}
                          </span>
                        )}
                        <StatusBadge 
                          status={member.status === 'ACTIVE' ? 'active' : member.status === 'INACTIVE' ? 'inactive' : 'pending'} 
                          label={POSITION_STATUS_LABELS[member.status] || member.status}
                          className="!px-2 !py-0.5 text-[9px]"
                        />
                      </div>

                      {/* Actions - always at bottom */}
                      <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
                        <button
                          onClick={() => openEditForm(member)}
                          className="flex items-center gap-1 rounded-xl bg-blue-50 px-3 py-2 text-xs font-bold text-blue-600 transition hover:bg-blue-100"
                        >
                          <Icon name="edit" className="h-3.5 w-3.5" />
                          ویرایش
                        </button>
                        <button
                          onClick={() => setDeleteTarget(member.id)}
                          className="flex items-center gap-1 rounded-xl bg-red-50 px-3 py-2 text-xs font-bold text-red-500 transition hover:bg-red-100"
                        >
                          <Icon name="trash" className="h-3.5 w-3.5" />
                          حذف
                        </button>
                      </div>
                    </div>

                    {/* Bottom line */}
                    <div className="absolute inset-x-0 bottom-0 h-0.5 origin-right scale-x-0 bg-gradient-to-l from-emerald-500 to-lime-400 transition-transform duration-500 group-hover:scale-x-100" />
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* =========================================================
          Form Modal
      ========================================================= */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? 'ویرایش عضو' : 'افزودن عضو جدید'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600 animate-fade-in">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* عنوان سمت */}
            <Input
              label="عنوان سمت"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              className="col-span-2"
            />

            {/* انتخاب کاربر (اختیاری) */}
            <div className="col-span-2">
              <label className="mb-1.5 block text-sm font-bold text-slate-700">
                انتخاب کاربر (اختیاری)
              </label>
              <select
                className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
                value={form.userId}
                onChange={(e) => setForm({ ...form, userId: e.target.value })}
              >
                <option value="">بدون کاربر</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            {/* نام و نام خانوادگی (جایگزین کاربر) */}
            <Input
              label="نام"
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            />
            <Input
              label="نام خانوادگی"
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            />

            {/* نوع سمت */}
            <select
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              <option value="BOARD">هیئت رئیسه</option>
              <option value="COMMITTEE">کمیته</option>
            </select>

            {/* ترتیب */}
            <Input
              label="ترتیب نمایش (عدد کوچک‌تر = بالاتر)"
              type="number"
              min="0"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
            />

            {/* تاریخ‌ها */}
            <JalaliDatePicker
              label="تاریخ شروع"
              value={form.startDate}
              onChange={(date) => setForm({ ...form, startDate: date })}
            />
            <JalaliDatePicker
              label="تاریخ پایان"
              value={form.endDate}
              onChange={(date) => setForm({ ...form, endDate: date })}
            />

            {/* وضعیت */}
            <select
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="ACTIVE">فعال</option>
              <option value="INACTIVE">غیرفعال</option>
              <option value="EXPIRED">منقضی</option>
            </select>

            {/* تحصیلات */}
            <Input
              label="تحصیلات (مدرک تحصیلی)"
              value={form.education}
              onChange={(e) => setForm({ ...form, education: e.target.value })}
            />
            <Input
              label="رشته تحصیلی"
              value={form.fieldOfStudy}
              onChange={(e) => setForm({ ...form, fieldOfStudy: e.target.value })}
            />
            <Input
              label="سابقه کاری"
              value={form.workExperience}
              onChange={(e) => setForm({ ...form, workExperience: e.target.value })}
            />
            <Input
              label="شماره تماس داخلی"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <Input
              label="ایمیل داخلی"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <JalaliDatePicker
              label="تاریخ تولد"
              value={form.birthDate}
              onChange={(date) => setForm({ ...form, birthDate: date })}
            />
            <Input
              label="محل تولد"
              value={form.birthPlace}
              onChange={(e) => setForm({ ...form, birthPlace: e.target.value })}
            />
          </div>

          {/* توضیحات و بیوگرافی */}
          <div className="space-y-4">
            <Input
              label="توضیحات کوتاه"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <div>
              <label className="mb-1.5 block text-sm font-bold text-slate-700">بیوگرافی</label>
              <textarea
                rows="4"
                className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="بیوگرافی کامل عضو..."
              />
            </div>
            <FileUpload
              label="عکس"
              accept="image/*"
              preview={imagePreview}
              onFileSelect={(file) => {
                setImageFile(file);
                setImagePreview(URL.createObjectURL(file));
              }}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" type="button" onClick={() => setFormOpen(false)}>
              انصراف
            </Button>
            <GradientButton type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  در حال ذخیره...
                </>
              ) : (
                editing ? 'ذخیره تغییرات' : 'افزودن عضو'
              )}
            </GradientButton>
          </div>
        </form>
      </Modal>

      {/* =========================================================
          Delete Confirmation
      ========================================================= */}
      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="تأیید حذف">
        <p className="text-text-dark/70">آیا از حذف این عضو مطمئن هستید؟</p>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)}>انصراف</Button>
          <GradientButton onClick={handleDelete}>تأیید حذف</GradientButton>
        </div>
      </Modal>
    </div>
  );
}